import { StyleSheet, css } from 'aphrodite';
import type { FC } from 'react';

import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import type { ISkipReason } from '../../viewModels/queue';

/**
 * Skip-reason picker dialog. Mirrors the legacy queue's skip modal — splits
 * `skipReasons` (permanent, `excludesFromPool === true`) from `tempSkipReasons`
 * (temporary) so the rep sees both buckets but can tell the lead-removal cost
 * apart. Picking a row fires `onPick(reason)` and the calling site owns the
 * actual `useSkipLead.mutateAsync` call so it can route follow-up handling
 * (advance to next lead, latch pending-outcome, etc.) per entry-point.
 *
 * Loading / error states are passed in by the caller. The dialog is purely
 * presentational so both entry points (TopBar "Skip lead" and Wrap-up's
 * BadLead path) can share it without duplicating fetch wiring.
 */
export type SkipReasonDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	skipReasons: ISkipReason[];
	tempSkipReasons: ISkipReason[];
	loading?: boolean;
	error?: string;
	/** Disabled while the parent's skip mutation is pending so the rep can't double-pick. */
	pendingReasonId?: string | null;
	onPick: (reason: ISkipReason) => void;
};

export const SkipReasonDialog: FC<SkipReasonDialogProps> = ({
	open,
	onOpenChange,
	skipReasons,
	tempSkipReasons,
	loading,
	error,
	pendingReasonId,
	onPick,
}) => {
	const hasAnyReasons = skipReasons.length > 0 || tempSkipReasons.length > 0;
	const isAnyPending = pendingReasonId !== null && pendingReasonId !== undefined;
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup styles={[styles.popup]}>
				<DialogHeader>
					<DialogTitle>Skip lead</DialogTitle>
				</DialogHeader>
				<DialogBody>
					{loading ? (
						<div className={css(styles.empty)}>Loading skip reasons…</div>
					) : error ? (
						<div className={css(styles.empty)}>
							<div className={css(styles.errorText)}>{error}</div>
						</div>
					) : !hasAnyReasons ? (
						<div className={css(styles.empty)}>No skip reasons configured for this account.</div>
					) : (
						<div className={css(styles.groups)}>
							{skipReasons.length > 0 && (
								<section className={css(styles.group)}>
									<h3 className={css(styles.groupHeader)}>Skip</h3>
									<p className={css(styles.groupHint)}>Removes this lead from the dialing pool.</p>
									<div className={css(styles.list)}>
										{skipReasons.map(r => (
											<SkipReasonRow
												key={r.id}
												reason={r}
												disabled={isAnyPending}
												pending={pendingReasonId === r.id}
												onPick={onPick}
											/>
										))}
									</div>
								</section>
							)}
							{tempSkipReasons.length > 0 && (
								<section className={css(styles.group)}>
									<h3 className={css(styles.groupHeader)}>Temporarily skip</h3>
									<p className={css(styles.groupHint)}>Lead stays in the pool and can resurface later.</p>
									<div className={css(styles.list)}>
										{tempSkipReasons.map(r => (
											<SkipReasonRow
												key={r.id}
												reason={r}
												disabled={isAnyPending}
												pending={pendingReasonId === r.id}
												onPick={onPick}
											/>
										))}
									</div>
								</section>
							)}
						</div>
					)}
				</DialogBody>
				<DialogFooter>
					<Button variant='outline' onClick={() => onOpenChange(false)} disabled={isAnyPending}>
						Cancel
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

type SkipReasonRowProps = {
	reason: ISkipReason;
	disabled: boolean;
	pending: boolean;
	onPick: (reason: ISkipReason) => void;
};

const SkipReasonRow: FC<SkipReasonRowProps> = ({ reason, disabled, pending, onPick }) => {
	return (
		<Button
			variant='outline'
			size='sm'
			disabled={disabled}
			onClick={() => onPick(reason)}
			styles={[styles.rowBtn]}
			aria-label={`Skip with reason: ${reason.label}`}
		>
			<span className={css(styles.rowMain)}>
				<span className={css(styles.rowLabel)}>{reason.label}</span>
				{reason.description && <span className={css(styles.rowDescription)}>{reason.description}</span>}
			</span>
			{pending && <span className={css(styles.rowPending)}>Skipping…</span>}
		</Button>
	);
};

const styles = StyleSheet.create({
	popup: {
		maxWidth: 520,
	},
	empty: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: 12,
		padding: '24px 8px',
		fontSize: 13,
		color: 'var(--ink-muted)',
	},
	errorText: {
		color: 'var(--ink)',
	},
	groups: {
		display: 'flex',
		flexDirection: 'column',
		gap: 18,
	},
	group: {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
	},
	groupHeader: {
		margin: 0,
		fontSize: 12,
		fontWeight: 600,
		textTransform: 'uppercase',
		letterSpacing: '0.06em',
		color: 'var(--ink-muted)',
	},
	groupHint: {
		margin: 0,
		fontSize: 12,
		color: 'var(--ink-muted)',
	},
	list: {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
		marginTop: 6,
	},
	rowBtn: {
		justifyContent: 'space-between',
		textAlign: 'left',
		padding: '10px 12px',
		height: 'auto',
		whiteSpace: 'normal',
	},
	rowMain: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		minWidth: 0,
	},
	rowLabel: {
		fontSize: 13,
		fontWeight: 500,
		color: 'var(--ink)',
	},
	rowDescription: {
		fontSize: 12,
		color: 'var(--ink-muted)',
	},
	rowPending: {
		fontSize: 12,
		color: 'var(--ink-muted)',
		marginLeft: 8,
		flexShrink: 0,
	},
});
